import React, { useEffect, useRef, useState } from 'react';
import styled from 'libs/styled';
import { shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { inServer } from '@fogia/core-web/constants';
import { buildQueryString } from '@fogia/core-web/libs/GrebbCommerceAPI/util';
import getQueryParams from '@fogia/core-web/utils/getQueryParams';
import cloneDeep from '@fogia/core-web/utils/cloneDeep';
import useSaveResponse from '@fogia/core-web/hooks/useSaveResponse';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import searchDispatch from '@fogia/core-web/state/models/Search/dispatches/search';
import clearDispatch from '@fogia/core-web/state/models/Search/dispatches/clear';

import filters from 'config/algolia/filters';
import sorts from 'config/algolia/sorts';

import CmsModules from '@fogia/core-web/libs/sanity/content/CmsModules';
import FilterContainer from '@fogia/core-web/containers/FilterContainer';
import SearchInputField from 'components/SearchInputField';
import CategoryModule from 'components/Category/Category';
import Pagination from 'components/Pagination';
import Breadcrumbs from 'components/Breadcrumbs';
import NoResults from './NoResults';

const INPUT_TYPOGRAPHY = ['Primary/24_110_510_-3', null, null, null, null, 'Primary/30_100_510_-5'];
const ACTION_BUTTON_TYPOGRAPHY = ['Primary/Mobile/13_100_510_-1', null, null, null, null, 'Primary/15_120_510_-1'];

const Wrapper = styled('div')``;
const SearchHeader = styled('div')`
    padding: 10px;
`;

const Search = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const wrapperRef = useRef({});
    const inputRef = useRef(null);
    const searchTimeout = useRef(null);

    const location = useLocation();
    const params = getQueryParams(location);
    let search = params.search || '';

    try {
        search = decodeURI(search.substring(0, 150));
    } catch (e) {
        // console.error(e);
    }

    const [localSearch, setLocalSearch] = useState(search);

    const pageContent = useAppSelector(({ page }) => page?.data?.pageContent, shallowEqual);

    // Save response from container
    const [savedProducts, saveProductsResponse] = useSaveResponse();
    const pageSearch = useAppSelector(({ page }) => page.search);
    const [currentPageProducts, setCurrentPageProducts] = useState(() => {
        const search = inServer ? pageSearch : window.location.search;
        const pageParam = new URLSearchParams(search || '').get('page');
        const parsed = parseInt(pageParam, 10);
        return parsed > 1 ? parsed - 1 : 0;
    });
    const [pageCountProducts, setPageCountProducts] = useState(0);
    const [hasReceivedResponse, setHasReceivedResponse] = useState(false);
    const [hitsCount, setHitsCount] = useState(null);
    const productsPerPage = 20;

    const siteSearch = (query) => {
        const currentParams = getQueryParams(location);
        let locationClone = cloneDeep(location);

        delete currentParams.page;
        setCurrentPageProducts(0);

        const updatedQueryParams = `?${buildQueryString(currentParams)}`;
        locationClone = { ...locationClone, search: updatedQueryParams };

        searchDispatch(query, history, locationClone);

        if (query && query.length < 1) {
            clearDispatch(history, location);
        }
    };

    // Debounced search dispatch
    useEffect(() => {
        if (localSearch !== search) {
            clearTimeout(searchTimeout.current);

            searchTimeout.current = setTimeout(() => {
                siteSearch(localSearch);
            }, 500);

            return () => clearTimeout(searchTimeout.current);
        }
    }, [localSearch]);

    // Sync localSearch when URL search param changes (e.g. browser back/forward)
    useEffect(() => {
        setLocalSearch(search);
    }, [search]);

    const handleInputChange = (value) => {
        const trimmedValue = value.replace(/^\s+/, '');
        setLocalSearch(trimmedValue);
    };

    const handleEditSearch = () => {
        setLocalSearch('');
        inputRef.current?.focus();
    };

    const scrollToTop = (next, smooth = false) => {
        // Scroll back to top of wrapper on pagination
        const headerHeight =
            (!inServer && getComputedStyle(document?.documentElement).getPropertyValue('--header-height')) || 0;
        setCurrentPageProducts(next);
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                smooth,
                top: wrapperRef.current.offsetTop - headerHeight,
            });
        }
    };

    const placeholderText = t('search.placeholder');

    return (
        <>
            <Breadcrumbs />
            <Wrapper ref={wrapperRef}>
                <SearchHeader>
                    <SearchInputField
                        ref={inputRef}
                        type="text"
                        placeholder={placeholderText}
                        aria-label={placeholderText}
                        value={localSearch}
                        onChange={handleInputChange}
                        typography={INPUT_TYPOGRAPHY}
                        action={
                            localSearch
                                ? {
                                      label: t('search.edit_search'),
                                      onClick: handleEditSearch,
                                      typography: ACTION_BUTTON_TYPOGRAPHY,
                                  }
                                : undefined
                        }
                    />
                </SearchHeader>

                {hasReceivedResponse && pageCountProducts === 0 && <NoResults searchTerm={search} />}

                <Wrapper>
                    <FilterContainer
                        config={filters}
                        pageSize={productsPerPage}
                        pagination={currentPageProducts}
                        sorts={sorts}
                        search={search}
                        isSearchPage
                        saveSortState={false}
                        render={({ response }) => {
                            saveProductsResponse(response);
                            if (response) {
                                setPageCountProducts(response.pageCount);
                                setCurrentPageProducts(response.page);
                                setHitsCount(response.hitsCount);
                                setHasReceivedResponse(true);
                            }

                            return (
                                savedProducts.current.products && (
                                    <CategoryModule config={filters} response={savedProducts.current} hideFilterBar />
                                )
                            );
                        }}
                    />
                </Wrapper>

                {pageCountProducts > 0 && (
                    <Pagination
                        handleClick={scrollToTop}
                        page={currentPageProducts}
                        pageCount={pageCountProducts}
                        pageSize={productsPerPage}
                        hitsCount={hitsCount}
                    />
                )}
                {pageContent && <CmsModules data={pageContent} />}
            </Wrapper>
        </>
    );
};
export default Search;
